import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import * as React from 'react';
import type { Role } from '@cfl/shared';

import { api, ApiError } from '@/lib/api';

/**
 * The signed-in employee, as resolved by GET /auth/me.
 *
 * `hasReports` is sent by the server rather than inferred from `role`, because in
 * this system being someone's manager is a fact about the reporting tree, not a
 * permission tier - a person can hold role EMPLOYEE and still have direct reports.
 * The Team section of the nav keys off this, never off role === 'MANAGER'.
 */
export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  departmentId: string | null;
  departmentName: string | null;
  designationTitle: string | null;
  reportingManagerId: string | null;
  reportingManagerName: string | null;
  hasReports: boolean;
  directReportCount: number;
  avatarUrl: string | null;
  /**
   * The account exists but HR has not finished setting it up - no department, or no
   * reporting manager. Sign-in still succeeds by design (auto-provisioning means
   * every valid @innoctive.com account gets a record), but goals cannot be assigned
   * and no assessment can be reviewed until it is resolved.
   */
  needsOnboarding: boolean;
  /** Has a password for the password sign-in. Shows the Change password menu item. */
  hasPassword: boolean;
}

/** Which sign-in methods the API has switched on (AUTH_PROVIDERS), from GET /auth/config. */
export interface AuthConfig {
  googleLogin: boolean;
  passwordLogin: boolean;
}

/**
 * Read at runtime rather than baked in at build time, so switching a deploy to password
 * sign-in is an API env change and a restart - no SPA rebuild. Until it answers (or if
 * it cannot), callers fall back to Google only, which is what every deploy is by default.
 */
export function useAuthConfig(): AuthConfig {
  const { data } = useQuery<AuthConfig, ApiError>({
    queryKey: ['auth', 'config'],
    queryFn: () => api.get<AuthConfig>('/auth/config'),
    staleTime: Infinity,
  });
  return data ?? { googleLogin: true, passwordLogin: false };
}

export type SessionState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  /** Employee record is EXITED. Credentials are valid, so the login screen would loop. */
  | { status: 'disabled' }
  | { status: 'authenticated'; user: SessionUser };

const SessionContext = React.createContext<SessionState | null>(null);

export const SESSION_QUERY_KEY = ['auth', 'me'] as const;

function useSessionQuery(): UseQueryResult<SessionUser, ApiError> {
  return useQuery<SessionUser, ApiError>({
    queryKey: SESSION_QUERY_KEY,
    queryFn: () => api.get<SessionUser>('/auth/me'),
    // A 401 is a real answer ("not signed in"), not a transient failure worth retrying.
    retry: (failureCount, error) =>
      !(error instanceof ApiError && (error.isUnauthenticated || error.isAccountDisabled)) &&
      failureCount < 2,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const { data, error, isPending } = useSessionQuery();

  const state = React.useMemo<SessionState>(() => {
    if (isPending) return { status: 'loading' };
    // The definite answers go before `data`: a failed refetch keeps the last user in `data`,
    // so checking it first left an expired or exited session looking signed in. Any other
    // error (the API restarting) keeps them signed in on the data they had.
    if (error?.isAccountDisabled) return { status: 'disabled' };
    if (error?.isUnauthenticated) return { status: 'unauthenticated' };
    if (data) return { status: 'authenticated', user: data };
    return { status: 'unauthenticated' };
  }, [data, error, isPending]);

  return <SessionContext.Provider value={state}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = React.useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}

/** The signed-in user, for components that only ever render inside the authed shell. */
export function useCurrentUser(): SessionUser {
  const session = useSession();
  if (session.status !== 'authenticated') {
    throw new Error('useCurrentUser called outside an authenticated route');
  }
  return session.user;
}

/** `to` lets a caller land somewhere other than plain /login, e.g. with a notice. */
export function useSignOut(): (to?: string) => Promise<void> {
  const queryClient = useQueryClient();
  return React.useCallback(async (to = '/login') => {
    try {
      await api.post('/auth/logout');
    } catch {
      // The session may already be gone server-side, or the network may be down.
      // Either way the local sign-out below still has to happen - refusing to sign
      // someone out because the logout request failed is the worst of both.
    }

    // Drop every cached query, not just the session: leaving another employee's
    // appraisal data in the cache across a sign-out on a shared machine would be a
    // real leak, and this app holds salary recommendations.
    queryClient.clear();

    /**
     * A full document load, not a router navigate.
     *
     * clear() empties the cache, but a mounted observer keeps returning the result
     * it is already holding - so SessionProvider stayed 'authenticated', RequireAuth
     * kept rendering the shell, and clicking Sign out appeared to do nothing until
     * the page was refreshed by hand. (The server side was working the whole time,
     * which is why the refresh landed on the login screen.)
     *
     * Reloading re-derives the session from /auth/me instead of from a cache
     * entry, and drops every other employee's data still sitting in React state -
     * which is what the clear() above was reaching for and cannot guarantee on its
     * own. Sign-out is the one action where throwing the whole page away is the
     * feature, not a cost.
     */
    window.location.assign(to);
  }, [queryClient]);
}
